import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} FloatingCartBtnRefs
 * @property {HTMLButtonElement} floatingBtn - The floating cart button
 */

/**
 * A floating add-to-cart button fixed at the bottom-right corner.
 * Clicks add the current variant to cart and redirect to /cart.
 *
 * @extends {Component<FloatingCartBtnRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['floatingBtn'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {IntersectionObserver|null} */
  #observer = null;

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Hide floating button when inline ATC button is visible
    const inlineAtc = target?.querySelector('.buy-buttons-block add-to-cart-component') || target?.querySelector('.buy-buttons-block');
    if (inlineAtc) {
      const rect = inlineAtc.getBoundingClientRect();
      const isInitiallyVisible = rect.top < window.innerHeight && rect.bottom > 0;
      this.classList.toggle('is-hidden', isInitiallyVisible);

      this.#observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-hidden', entry.isIntersecting);
      }, { threshold: 0 });
      this.#observer.observe(inlineAtc);

      // 动态模块加载可能撑大容器导致布局偏移，监听容器尺寸变化来兜底
      if (target) {
        this.#resizeObserver = new ResizeObserver(() => {
          const r = inlineAtc.getBoundingClientRect();
          this.classList.toggle('is-hidden', r.top < window.innerHeight && r.bottom > 0);
        });
        this.#resizeObserver.observe(target);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Handles the floating button click: add to cart then redirect to /cart
   */
  handleAddToCartClick = async () => {
    const btn = this.refs.floatingBtn;
    const variantId = this.dataset.currentVariantId;

    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    btn.dataset.loading = 'true';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });

      const data = await response.json();

      if (data.status) {
        // Error from Shopify (e.g. sold out)
        btn.disabled = false;
        btn.dataset.loading = 'false';
        return;
      }

      window.location.href = '/cart';
    } catch {
      btn.disabled = false;
      btn.dataset.loading = 'false';
    }
  };

  /**
   * Handles variant update events to keep variant ID in sync
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
      this.dataset.variantAvailable = variant.available ? 'true' : 'false';
      this.refs.floatingBtn.disabled = !variant.available;
    } else {
      this.dataset.currentVariantId = '';
      this.dataset.variantAvailable = 'false';
      this.refs.floatingBtn.disabled = true;
    }
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
